<script setup>
import { computed, defineProps, ref } from "vue";
import { Icon } from "@iconify/vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";

const props = defineProps({
  route: {
    type: Object,
    required: true,
  },
});

const store = useStore();
const route = useRoute();

const isHamburgerMenuOpen = computed(
  () => store.state.uiSlice.isHamburgerMenuOpen,
);
const isOpen = ref(false);

// Computed properties for icon and menu name
const menuName = computed(() => props.route.meta.menuName || "");
const icon = computed(() => props.route.meta.icon || "mdi:folder");

// Toggle the open state for the route's children
const toggleOpen = () => {
  if (!isHamburgerMenuOpen.value) {
    store.commit("uiSlice/setHamburgerMenuOpen", true);
    isOpen.value = true;
  } else {
    isOpen.value = !isOpen.value;
  }
};

const isActive = computed(() => route.fullPath.includes(props.route.path));
</script>

<template>
  <div
    v-if="props.route.children && props.route.children.length > 0"
    class="space-y-1"
  >
    <!-- Parent route with collapsible children -->
    <div
      class="flex items-center justify-between transition-colors p-2 cursor-pointer hover:bg-icons-bg rounded-lg dark:hover:bg-neutral-700 dark:text-neutral-400"
      :class="{ 'hamburger-active': isActive }"
      @click="toggleOpen"
      aria-controls="route-children"
      :aria-expanded="isOpen ? 'true' : 'false'"
    >
      <div class="flex items-center gap-2 select-none">
        <Icon :icon="icon" class="text-3xl" />
        <span
          :class="{ hidden: !isHamburgerMenuOpen, block: isHamburgerMenuOpen }"
          class="select-none"
        >
          {{ menuName }}
        </span>
      </div>
      <Icon
        :icon="isOpen ? 'mdi:chevron-up' : 'mdi:chevron-down'"
        class="text-2xl"
        v-show="isHamburgerMenuOpen"
      />
    </div>

    <!-- Children list -->
    <div
      class="transition-all overflow-hidden w-full space-y-1 pr-5"
      :style="
        isOpen && isHamburgerMenuOpen ? 'max-height: 1000px;' : 'max-height: 0;'
      "
    >
      <router-link
        v-for="child in props.route.children"
        :key="child.name || child.path"
        :to="{ name: child.name }"
        class="flex w-full items-center gap-2 py-3 pr-2 rounded-lg hover:bg-icons-bg"
        active-class="hamburger-child-active"
      >
        <Icon :icon="child.meta?.icon || 'mdi:dot'" class="text-xl" />
        <span class="text-sm select-none">{{ child.meta?.menuName }}</span>
      </router-link>
    </div>
  </div>

  <!-- Single route (no children) -->
  <router-link
    v-else
    :to="props.route.path"
    class="flex gap-2 items-center justify-start transition-colors p-2 cursor-pointer hover:bg-icons-bg rounded-lg dark:hover:bg-neutral-700 dark:text-neutral-400"
    active-class="hamburger-active"
  >
    <Icon :icon="icon" class="text-3xl" />
    <span
      :class="{ hidden: !isHamburgerMenuOpen, block: isHamburgerMenuOpen }"
      class="select-none"
    >
      {{ menuName }}
    </span>
  </router-link>
</template>
